// Cleans rich-text HTML from the CMS before it's rendered or saved: formatting, links and images only, no scripts
import DOMPurify from "dompurify"

const ALLOWED_TAGS = [
  "p", "br", "hr", "div", "span",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "strong", "b", "em", "i", "u", "s", "strike", "sub", "sup", "small", "mark",
  "blockquote", "pre", "code",
  "ul", "ol", "li", "dl", "dt", "dd",
  "a", "img", "figure", "figcaption",
  "table", "thead", "tbody", "tfoot", "tr", "th", "td", "caption", "colgroup", "col",
]

const ALLOWED_ATTR = [
  "href", "target", "rel", "title",
  "src", "alt", "width", "height",
  "colspan", "rowspan", "align",
  "style", "class", "dir", "lang",
]

// Links and images: web, mail, phone and same-site addresses only, never javascript: or data:
const ALLOWED_URI = /^(?:https?:|mailto:|tel:|\/|#|\.{0,2}\/)/i

// Inline styles keep only layout the editor writes itself (image width, alignment)
const ALLOWED_STYLE = new Set(["width", "max-width", "height", "text-align", "float", "margin", "margin-left", "margin-right"])
const SAFE_STYLE_VALUE = /^[\w\s.%#,-]+$/

let hooked = false
function addHooks() {
  if (hooked) return
  hooked = true
  DOMPurify.addHook("uponSanitizeAttribute", (_node, data) => {
    if (data.attrName !== "style") return
    const kept = data.attrValue
      .split(";")
      .map(d => d.split(":"))
      .filter(([prop, value]) => prop && value && ALLOWED_STYLE.has(prop.trim().toLowerCase()) && SAFE_STYLE_VALUE.test(value.trim()))
      .map(([prop, value]) => `${prop.trim().toLowerCase()}: ${value.trim()}`)
    if (kept.length) data.attrValue = kept.join("; ")
    else data.keepAttr = false
  })
  // A link that opens a new tab still does, but can't reach back into the app
  const newTab = new WeakSet<Element>()
  DOMPurify.addHook("beforeSanitizeAttributes", node => {
    if (node.tagName === "A" && node.hasAttribute("target")) newTab.add(node)
  })
  DOMPurify.addHook("afterSanitizeAttributes", node => {
    if (!newTab.has(node)) return
    node.setAttribute("target", "_blank")
    node.setAttribute("rel", "noopener noreferrer")
  })
}

// Saving re-checks every article on each edit, so unchanged bodies come from here
const cache = new Map<string, string>()
const CACHE_MAX = 1000

export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return ""
  const input = String(html)
  // Without a DOM to parse with, keep only the text
  if (typeof window === "undefined" || !DOMPurify.isSupported) return input.replace(/<[^>]*>/g, "")
  const hit = cache.get(input)
  if (hit !== undefined) return hit
  addHooks()
  const clean = DOMPurify.sanitize(input, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP: ALLOWED_URI,
    ALLOW_DATA_ATTR: false,
  })
  if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value!)
  cache.set(input, clean)
  return clean
}
